import { useOnChange } from "@prismicio/editor-support/React"
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  type IconName,
  type MediaAssetType,
  Tooltip,
} from "@prismicio/editor-ui"
import { forwardRef, useState } from "react"

import { useMediaLibrary } from "../../MediaLibraryContext"

interface AssetTypeMetadata {
  name?: string
  icon: IconName
}

const assetTypesMetadata: Record<MediaAssetType, AssetTypeMetadata> = {
  all: { name: "All media", icon: "public" },
  image: { name: "Images", icon: "image" },
  video: { name: "Videos", icon: "videocam" },
  document: { name: "Documents", icon: "insertDriveFile" },
  audio: { name: "Audio files", icon: "musicNote" },
}

const assetTypesMetadataEntries = Object.entries(assetTypesMetadata) as [
  MediaAssetType,
  AssetTypeMetadata,
][]

export function AssetTypeFilter() {
  const { assetType, initialAssetType, onFilterChange } = useMediaLibrary()

  const [optimisticAssetType, setOptimisticAssetType] = useState(assetType)
  useOnChange(assetType, () => {
    setOptimisticAssetType(assetType)
  })

  const [dropdownOpen, setDropdownOpen] = useState(false)

  function onAssetTypeToggle(newAssetType: MediaAssetType) {
    setDropdownOpen(false)
    setOptimisticAssetType(newAssetType)
    onFilterChange({ assetType: newAssetType })
  }

  const { icon, name } = assetTypesMetadata[optimisticAssetType]
  const tooltipAssetTypeName = assetTypesMetadata[initialAssetType].name
  const isDropdownDisabled = initialAssetType !== "all"

  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <DropdownMenuTrigger disabled={isDropdownDisabled}>
        <AssetTypeFilterDropdownButton
          icon={icon}
          isDropdownDisabled={isDropdownDisabled}
          buttonName={name}
          tooltipAssetTypeName={tooltipAssetTypeName}
        />
      </DropdownMenuTrigger>

      <DropdownMenuContent minWidth={256} align="start" childrenFocusScope>
        <>
          {assetTypesMetadataEntries
            .filter(
              ([dropdownAssetType]) =>
                dropdownAssetType !== optimisticAssetType,
            )
            .map(([dropdownAssetType, { icon, name }]) => (
              <DropdownMenuItem
                key={dropdownAssetType}
                startIcon={<Icon name={icon} />}
                onSelect={() => onAssetTypeToggle(dropdownAssetType)}
              >
                {name}
              </DropdownMenuItem>
            ))}
        </>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface AssetTypeFilterDropdownButtonProps {
  icon: IconName
  isDropdownDisabled: boolean
  buttonName?: string
  tooltipAssetTypeName?: string
}

const AssetTypeFilterDropdownButton = forwardRef<
  HTMLButtonElement,
  AssetTypeFilterDropdownButtonProps
>(function AssetTypeFilterDropdownButton(props, ref) {
  const {
    icon,
    isDropdownDisabled,
    buttonName,
    tooltipAssetTypeName,
    ...rest
  } = props

  return (
    <Tooltip
      content={`You can only pick ${
        tooltipAssetTypeName?.toLocaleLowerCase() ?? "this type of media"
      }`}
      visible={isDropdownDisabled}
    >
      <Button
        startIcon={icon}
        endIcon="arrowDropDown"
        size="small"
        color="grey"
        disabled={isDropdownDisabled}
        ref={ref}
        // rest added to make the grandparent DropdownMenuTrigger work with asChild
        {...rest}
      >
        {buttonName}
      </Button>
    </Tooltip>
  )
})
